"use client";

import * as React from "react";
import { cn } from "@repo/utils/cn";
import { ErrorIcon } from "@repo/ui-parkmobile/icons/ErrorIcon";
import { Collapsible as CollapsiblePrimitive } from "radix-ui";
import { ComponentProps, useRef, useEffect, useState } from "react";
import { ChevronIcon } from "@repo/ui-parkmobile/icons/ChevronIcon";

function useOnClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: (event: MouseEvent | TouchEvent) => void,
) {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

export type DropdownOption = {
  label: string;
  value: string;
  icon?: React.ReactNode;
  disabled?: boolean;
};

export type DropdownProps = {
  trigger: React.ReactNode;
  options: DropdownOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  state?: "default" | "error";
  maxHeight?: number;
  className?: string;
  contentClassName?: string;
  showNoneOption?: boolean;
  noneOptionLabel?: string;
  align?: "start" | "end" | "center";
  renderOption?: (
    option: DropdownOption,
    isSelected: boolean,
  ) => React.ReactNode;
} & ComponentProps<typeof CollapsiblePrimitive.Root>;

export function Dropdown({
  trigger,
  options,
  value,
  onValueChange,
  placeholder,
  state = "default",
  maxHeight = 240,
  className,
  contentClassName,
  showNoneOption = true,
  noneOptionLabel = "None selected",
  align = "start",
  renderOption,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: DropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = (newOpenState: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpenState);
    }
    controlledOnOpenChange?.(newOpenState);
  };

  const selectedOption = options.find((opt) => opt.value === value);

  useOnClickOutside(containerRef, () => {
    if (open) {
      handleOpenChange(false);
    }
  });

  const renderDefaultOption = (option: DropdownOption, isSelected: boolean) => (
    <div className="uipm:flex uipm:items-center uipm:gap-3 uipm:w-full">
      {option.icon && <div className="uipm:shrink-0">{option.icon}</div>}
      <span className="uipm:truncate uipm:flex-1 uipm:font-['Inter'] uipm:text-[length:var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:font-medium uipm:text-[var(--typography-colors-text-faded-dark,#575757)]">
        {option.label}
      </span>
    </div>
  );

  const handleOptionClick = (optionValue: string) => {
    const option = options.find((opt) => opt.value === optionValue);
    if (option?.disabled) return;

    onValueChange?.(optionValue);
    handleOpenChange(false);
  };

  return (
    <div
      ref={containerRef}
      className={cn("uipm:relative uipm:w-full", className)}
    >
      <CollapsiblePrimitive.Root
        open={open}
        onOpenChange={handleOpenChange}
        className="uipm:flex uipm:w-full uipm:flex-col"
        {...props}
      >
        <CollapsiblePrimitive.Trigger asChild>
          {trigger}
        </CollapsiblePrimitive.Trigger>

        <CollapsiblePrimitive.Content
          className={cn(
            "uipm:absolute uipm:top-full uipm:mt-2 uipm:z-50 uipm:bg-[var(--surface-surface-primary)] uipm:rounded-xl uipm:shadow-lg",
            "uipm:border-2 uipm:border-[var(--color-color-light)] uipm:py-2",
            "uipm:data-[state=open]:animate-in uipm:data-[state=closed]:animate-out uipm:data-[state=closed]:fade-out-0 uipm:data-[state=open]:fade-in-0 uipm:data-[state=closed]:zoom-out-95 uipm:data-[state=open]:zoom-in-95",
            {
              "uipm:left-0": align === "start",
              "uipm:right-0": align === "end",
              "uipm:left-1/2 uipm:-translate-x-1/2": align === "center",
              "uipm:w-full": !contentClassName?.includes("uipm:w-"),
            },
            contentClassName,
          )}
        >
          <div
            className="uipm:max-h-60 uipm:overflow-y-auto uipm:rounded-lg uipm:bg-[var(--surface-surface-primary)]"
            style={{ maxHeight }}
          >
            {/* None selected option */}
            {showNoneOption && (
              <button
                type="button"
                className={cn(
                  "uipm:w-full uipm:flex uipm:items-center uipm:px-4 uipm:py-3 uipm:text-left uipm:text-base uipm:leading-[26px] uipm:font-medium uipm:transition-colors uipm:cursor-pointer",
                  "uipm:text-[var(--typography-colors-text-faded)]",
                  "uipm:hover:bg-[#F4F4F480]",
                  "uipm:active:bg-[#E0E0E080]",
                  "uipm:focus:outline-none uipm:focus:bg-[#F4F4F480]",
                  "uipm:rounded-t-lg",
                )}
                onClick={() => handleOptionClick("")}
              >
                <span className="uipm:truncate uipm:flex-1 uipm:text-[length:var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:font-medium uipm:text-[var(--typography-colors-text-faded)]">
                  {noneOptionLabel}
                </span>
              </button>
            )}

            {/* Regular options */}
            {options.map((option, index) => {
              const isSelected = value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  className={cn(
                    "uipm:w-full uipm:flex uipm:items-center uipm:px-4 uipm:py-3 uipm:text-left uipm:transition-colors uipm:cursor-pointer",
                    "uipm:font-['Inter'] uipm:text-[var(--typography-sizes-body-medium,16px)] uipm:leading-[var(--typography-line-heights-body-medium,26px)]",
                    "uipm:hover:bg-[#F4F4F480] uipm:hover:text-[var(--surface-surface-accent)] uipm:hover:font-medium",
                    "uipm:active:bg-[#E0E0E080]",
                    "uipm:focus:outline-none uipm:focus:bg-[#F4F4F480]",
                    "uipm:disabled:opacity-50 uipm:disabled:cursor-not-allowed",
                    isSelected
                      ? "uipm:text-[var(--surface-surface-accent)] uipm:font-medium"
                      : "uipm:text-[var(--typography-colors-text-faded-dark,#575757)] uipm:font-medium",
                    {
                      // First option - rounded top corners (only if no "None" option)
                      "uipm:rounded-t-lg": index === 0 && !showNoneOption,
                      // Last option - rounded bottom corners
                      "uipm:rounded-b-lg": index === options.length - 1,
                    },
                  )}
                  onClick={() => handleOptionClick(option.value)}
                >
                  {renderOption
                    ? renderOption(option, isSelected)
                    : renderDefaultOption(option, isSelected)}
                </button>
              );
            })}
          </div>
        </CollapsiblePrimitive.Content>
      </CollapsiblePrimitive.Root>
    </div>
  );
}

// Helper component for creating trigger buttons
export const DropdownTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> & {
    isOpen?: boolean;
    state?: "default" | "error";
    placeholder?: string;
    selectedLabel?: string;
    showChevron?: boolean;
    background?: "default" | "white";
  }
>(
  (
    {
      className,
      isOpen,
      state = "default",
      placeholder,
      selectedLabel,
      showChevron = true,
      background = "default",
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "uipm:relative uipm:flex uipm:items-center uipm:justify-between uipm:w-full uipm:px-4 uipm:py-4 uipm:min-h-[56px] uipm:rounded-xl uipm:text-left uipm:transition-colors",
        background === "white" ? "uipm:bg-white" : "uipm:form-hover",
        "uipm:border-2 uipm:border-transparent",
        "uipm:outline-none",
        "uipm:focus:bg-[var(--surface-surface-primary)] uipm:focus:border-[var(--color-color-light)]",
        "uipm:focus-visible:bg-[var(--surface-surface-primary)] uipm:focus-visible:border-[var(--color-color-light)]",
        "uipm:active:border-[var(--color-color-light)]",
        "uipm:text-base uipm:font-medium uipm:cursor-pointer",
        {
          "uipm:border-[var(--color-color-danger)] uipm:pr-20":
            state === "error",
          "uipm:bg-[var(--surface-surface-primary)] uipm:border-[var(--color-color-light)]":
            isOpen,
        },
        className,
      )}
      {...props}
    >
      <div className="uipm:flex uipm:items-center uipm:gap-2 uipm:flex-1 uipm:min-w-0">
        {children}
        {!children && (
          <span
            className={cn(
              "uipm:font-['Inter'] uipm:text-[length:var(--typography-sizes-body-medium,16px)] uipm:leading-[var(--typography-line-heights-body-medium,26px)] uipm:font-medium",
              selectedLabel
                ? "uipm:text-[var(--typography-colors-text-faded-dark,#575757)]"
                : "uipm:text-[var(--typography-colors-text-faded)]",
            )}
          >
            {selectedLabel || placeholder || "Select..."}
          </span>
        )}
      </div>
      <div className="uipm:absolute uipm:right-4 uipm:top-1/2 uipm:-translate-y-1/2 uipm:flex uipm:items-center uipm:gap-2">
        {state === "error" && <ErrorIcon />}
        {showChevron && (
          <ChevronIcon
            color="var(--color-color-accent)"
            className={cn(
              "uipm:w-4 uipm:h-[9px] uipm:transition-transform uipm:shrink-0",
              {
                "uipm:rotate-180": isOpen,
              },
            )}
          />
        )}
      </div>
    </button>
  ),
);
DropdownTrigger.displayName = "DropdownTrigger";
